/**
 * Adding a storage, export or edit location: a server folder, an S3 or OCI bucket (1.0.0-rc.9: also as an edit
 * location, Standard class only), or a folder on a storage agent; edit and storage locations get workstation mappings.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Cloud, HardDrive, Loader2, Plus, Server } from 'lucide-react';
import type { StoragePurpose } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { useAgents } from '../../../api/hooks';
import { FolderPicker, agentBaseFor, useMounts, type FolderSource } from '../../../components/FolderPicker';
import { Modal } from '../../../components/Modal';
import { Segmented } from '../../../components/Segmented';
import { toast } from '../../../components/Toasts';
import { queryKeys } from '../../../api/queryKeys';
import { WorkstationMappingsEditor, mappingProblems, mappingsBody, type MappingDraft } from '../../../components/WorkstationMappings';

type AddType = 'folder' | 's3' | 'oci' | 'agent';

export function AddLocationDialog({ purpose, onClose, agentId: presetAgent }: { purpose: StoragePurpose; onClose: () => void; agentId?: string }) {
  const qc = useQueryClient();
  const agents = (useAgents().data?.items ?? []).filter((a) => a.status === 'active');
  const [type, setType] = useState<AddType>(presetAgent ? 'agent' : 'folder');
  const [path, setPath] = useState('');
  const [name, setName] = useState('');
  const [site, setSite] = useState('local');
  const [description, setDescription] = useState('');
  const [agentId, setAgentId] = useState(presetAgent ?? '');
  const [mode, setMode] = useState<'managed' | 'in_place'>('managed');
  const [b, setB] = useState({ bucket: '', region: '', endpoint: '', namespace: '', prefix: '', accessKeyId: '', secretAccessKey: '', forcePathStyle: true });
  // Archive tiers (0.29.0), opt-in: Standard sends exactly what earlier releases sent.
  const [storageClass, setStorageClass] = useState<'STANDARD' | 'GLACIER_IR' | 'GLACIER' | 'DEEP_ARCHIVE'>('STANDARD');
  const [ociArchive, setOciArchive] = useState(false);
  const [restoreDays, setRestoreDays] = useState('');
  const [restoreTier, setRestoreTier] = useState<'Expedited' | 'Standard' | 'Bulk'>('Standard');
  const cold = (type === 's3' && (storageClass === 'GLACIER' || storageClass === 'DEEP_ARCHIVE')) || (type === 'oci' && ociArchive);
  const [saving, setSaving] = useState(false);
  // Edit locations (1.0.0-rc.6): how editors' computers open it (named mappings, 1.0.0-rc.9), and how long hand-offs stay.
  const edit = purpose === 'edit';
  const [mappings, setMappings] = useState<MappingDraft[]>([]);
  const [retention, setRetention] = useState('30');
  const agent = agents.find((a) => a.id === agentId);
  const source: FolderSource = type === 'agent' ? { type: 'agent', agentId, base: agentBaseFor(agent, purpose) } : { type: 'server', root: edit ? 'edit' : 'storage' };
  const listing = useMounts(path, (type === 'folder') || (type === 'agent' && Boolean(agentId)), source);
  useEffect(() => { if (path && !name) setName(path.split('/').pop() ?? ''); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (b.bucket && !name) setName(b.bucket); }, [b.bucket]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (agent) setSite(agent.site); }, [agent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = () => {
    const common = {
      name: name.trim(), site, description, purpose, ...(edit ? { retentionDays: Number(retention) || 30 } : {}),
      ...(edit && mappings.length ? { workstationMappings: mappingsBody(mappings) } : {}),
    };
    if (type === 'folder') return { type, ...common, path };
    if (type === 'agent') return { type, ...common, agentId, path, mode };
    const creds = { bucket: b.bucket.trim(), prefix: b.prefix.trim(), accessKeyId: b.accessKeyId.trim(), secretAccessKey: b.secretAccessKey };
    const days = cold && Number(restoreDays) >= 1 ? { restoreDays: Number(restoreDays) } : {};
    if (type === 'oci') return { type, ...common, ...creds, namespace: b.namespace.trim(), region: b.region.trim(), ...(ociArchive ? { archive: true, ...days } : {}) };
    return {
      type, ...common, ...creds, region: b.region.trim() || 'us-east-1', ...(b.endpoint.trim() ? { endpoint: b.endpoint.trim() } : {}), forcePathStyle: b.forcePathStyle,
      ...(storageClass !== 'STANDARD' ? { storageClass, ...(cold ? { ...days, restoreTier } : {}) } : {}),
    };
  };
  const ready = name.trim() && !mappingProblems(mappings).length && (type === 'folder' ? path !== '' && !listing.data?.location && listing.data?.writable !== false
    : type === 'agent' ? agentId && !listing.data?.location && (mode === 'in_place' || listing.data?.writable !== false)
      : b.bucket.trim() && b.accessKeyId.trim() && b.secretAccessKey && (type === 's3' || (b.namespace.trim() && b.region.trim())));

  async function submit() {
    setSaving(true);
    try {
      await api('/api/v1/storage-locations', { method: 'POST', json: body() });
      await qc.invalidateQueries({ queryKey: queryKeys.storage() });
      await qc.invalidateQueries({ queryKey: ['mounts'] });
      // A new export location is offered in the export dialog at once.
      if (purpose === 'export') await qc.invalidateQueries({ queryKey: queryKeys.exportDestinations() });
      if (edit) await qc.invalidateQueries({ queryKey: queryKeys.editDestinations() });
      toast(`Added ${name}`);
      onClose();
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  const bucketField = (key: keyof typeof b, label: string, props: Record<string, unknown> = {}) => (
    <div className="field"><label htmlFor={`b-${key}`}>{label}</label>
      <input id={`b-${key}`} className="input" value={String(b[key])} onChange={(e) => setB({ ...b, [key]: e.target.value })} autoComplete="off" {...props} /></div>
  );

  return (
    <Modal title={purpose === 'export' ? 'Add an export location' : edit ? 'Add an edit location' : 'Add storage'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!ready || saving} onClick={() => void submit()} data-testid="create-location">
        {saving ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} {type === 's3' || type === 'oci' ? 'Test and add' : 'Add'}
      </button>
    </>}>
      <Segmented role="radiogroup" style={{ marginBottom: 12 }} value={type} onChange={(t) => { setType(t); if (t === 'folder' || t === 'agent') setPath(''); }} options={[
        { value: 'folder', label: <><HardDrive size={13} /> Server folder</>, testId: 'add-type-folder' },
        // An edit bucket (1.0.0-rc.9): the S3 side of a NAS share editors open over SMB.
        { value: 's3' as const, label: <><Cloud size={13} /> S3 bucket</>, testId: 'add-type-s3' },
        { value: 'oci' as const, label: <><Cloud size={13} /> OCI bucket</>, testId: 'add-type-oci' },
        { value: 'agent', label: <><Server size={13} /> Storage agent folder</>, testId: 'add-type-agent' },
      ]} />
      {type === 'folder' && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>{edit
            ? 'A folder under the server’s edit root (PRISM_EDIT_ROOT): storage of its own, never inside or around PRISM’s media, storage or watch folders. Share it with the editors over SMB.'
            : 'A folder under the server’s storage root. Watch folders use a separate root, so storage is never ingested from.'}</p>
          <FolderPicker path={path} onPath={setPath} source={source} />
        </>
      )}
      {type === 'agent' && (
        <>
          <div className="field"><label htmlFor="add-agent">Storage agent</label>
            <select id="add-agent" className="select" value={agentId} onChange={(e) => { setAgentId(e.target.value); setPath(''); }}>
              <option value="">Choose an agent…</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.site}{a.connected ? '' : ' (disconnected)'}</option>)}
            </select>
            {agents.length === 0 && <span className="help">Enrol an agent in Administration → Storage agents first.</span>}</div>
          {purpose === 'storage' && (
            <div className="field"><label>How PRISM uses it</label>
              <Segmented role="radiogroup" value={mode} onChange={setMode} options={[
                { value: 'managed', label: 'PRISM-managed storage' }, { value: 'in_place', label: 'Archive catalogued in place' },
              ]} />
              <span className="help">{mode === 'managed' ? 'A storage tier on site: PRISM writes originals here and deletes them when assets are purged.' : 'PRISM catalogues the files already here and never moves or deletes them.'}</span></div>
          )}
          {agentId && <FolderPicker path={path} onPath={setPath} source={source} />}
        </>
      )}
      {type === 's3' && (
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {bucketField('bucket', 'Bucket', { placeholder: 'prism-media' })}
          {bucketField('region', 'Region', { placeholder: 'us-east-1' })}
          {bucketField('endpoint', 'Endpoint (for S3-compatible storage)', { placeholder: 'https://s3.example.com, empty for AWS' })}
          {bucketField('prefix', 'Folder in the bucket (optional)', { placeholder: 'prism/' })}
          {bucketField('accessKeyId', 'Access key', { className: 'input mono' })}
          {bucketField('secretAccessKey', 'Secret key', { type: 'password', className: 'input mono', autoComplete: 'new-password' })}
          <label className="toggle-row" style={{ gridColumn: '1 / -1' }}><input type="checkbox" checked={b.forcePathStyle} onChange={(e) => setB({ ...b, forcePathStyle: e.target.checked })} /><span>Path-style addressing (MinIO, Ceph and most S3-compatible storage)</span></label>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label htmlFor="b-class">Storage class</label>
            <select id="b-class" className="select" value={storageClass} onChange={(e) => { const v = e.target.value as typeof storageClass; setStorageClass(v); if (v === 'DEEP_ARCHIVE' && restoreTier === 'Expedited') setRestoreTier('Standard'); }} data-testid="add-storage-class">
              <option value="STANDARD">Standard (default)</option>
              <option value="GLACIER_IR">Glacier Instant Retrieval: read at once, cheaper storage, billed per retrieval</option>
              {purpose === 'storage' && <option value="GLACIER">Glacier Flexible Retrieval: an archive (restore takes minutes to hours)</option>}
              {purpose === 'storage' && <option value="DEEP_ARCHIVE">Glacier Deep Archive: an archive (restore takes up to 12–48 hours)</option>}
            </select>
            <span className="help">{cold ? 'An archive holds originals only (the originals tier or a tiering rule target). Files are restored before they can be read; PRISM then copies them to the originals tier.' : storageClass === 'GLACIER_IR' ? 'Files are sent with the GLACIER_IR storage class and read like any file. AWS bills at least 90 days per file.' : 'Files are stored with the bucket’s default class. Nothing changes from earlier releases.'}</span></div>
          {cold && <ArchiveRestoreFields days={restoreDays} onDays={setRestoreDays} max={30} tier={restoreTier} onTier={setRestoreTier} tiers={storageClass === 'DEEP_ARCHIVE' ? ['Standard', 'Bulk'] : ['Expedited', 'Standard', 'Bulk']} />}
        </div>
      )}
      {type === 'oci' && (
        <>
          <p className="muted" style={{ marginTop: 0 }}>PRISM uses OCI Object Storage through its S3-compatible API. Create a Customer Secret Key under your OCI user and use its access key and secret here.</p>
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {bucketField('namespace', 'Object Storage namespace', { placeholder: 'axaxnpcrorw5' })}
            {bucketField('region', 'Region', { placeholder: 'eu-frankfurt-1' })}
            {bucketField('bucket', 'Bucket')}
            {bucketField('prefix', 'Folder in the bucket (optional)')}
            {bucketField('accessKeyId', 'Access key', { className: 'input mono' })}
            {bucketField('secretAccessKey', 'Secret key', { type: 'password', className: 'input mono', autoComplete: 'new-password' })}
            {purpose === 'storage' && (
              <label className="toggle-row" style={{ gridColumn: '1 / -1' }}><input type="checkbox" checked={ociArchive} onChange={(e) => setOciArchive(e.target.checked)} data-testid="add-oci-archive" />
                <span>Archive tier: new files go to OCI Archive (cheapest; a restore takes up to several hours). Holds originals only. The Customer Secret Key’s user needs OBJECT_RESTORE.</span></label>
            )}
            {ociArchive && <ArchiveRestoreFields days={restoreDays} onDays={setRestoreDays} max={10} />}
          </div>
        </>
      )}
      {edit && (type === 's3' || type === 'oci') && (
        <div className="banner info" style={{ marginTop: 8 }} data-testid="edit-bucket-note"><div className="banner-body">
          <strong>A bucket as an edit location</strong>
          <span>Editors open the same files over SMB: use a NAS that serves one share over S3 and SMB (dual-protocol). Check it by writing a file through S3 and seeing it over SMB. PRISM never overwrites an object here. A NAS on your own network needs its host in <span className="mono">PRISM_ALLOW_PRIVATE_ENDPOINTS</span>.</span>
        </div></div>
      )}
      {(type === 's3' || type === 'oci') && <p className="faint" style={{ fontSize: 12, margin: '4px 0 0' }}>{cold ? 'PRISM opens the bucket before saving. An archive is never streamed from, so no byte-range probe is written to it.' : 'PRISM opens the bucket before saving and checks that byte ranges can be read, which proxies need to stream.'} The secret is stored encrypted with PRISM_SECRETS_KEY and never shown again.</p>}
      <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 14 }}>
        <div className="field"><label htmlFor="loc-name">Name</label><input id="loc-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></div>
        <div className="field"><label htmlFor="loc-site">Site</label><input id="loc-site" className="input" value={site} disabled={type === 'agent'} onChange={(e) => setSite(e.target.value.toLowerCase())} /></div>
      </div>
      {edit && (
        <>
          <div className="field" style={{ marginTop: 6 }}><label>Workstation mappings: how editors’ computers open it</label>
            <WorkstationMappingsEditor value={mappings} onChange={setMappings} purpose="edit" />
            <span className="help">The person sending picks one, and the timelines link the media at its paths, so editors open them without relinking.</span></div>
          <div className="field"><label htmlFor="loc-retention">Keep hand-offs for (days)</label>
            <input id="loc-retention" className="input" type="number" min={1} max={3650} value={retention} onChange={(e) => setRetention(e.target.value)} style={{ maxWidth: 140 }} /></div>
        </>
      )}
      <div className="field"><label htmlFor="loc-desc">Description</label><input id="loc-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={purpose === 'export' ? 'e.g. Playout drop folder' : 'e.g. RAID 6 array for hi-res masters'} /></div>
    </Modal>
  );
}

/** Restore defaults when adding an archive. */
function ArchiveRestoreFields({ days, onDays, max, tier, onTier, tiers }: {
  days: string; onDays: (v: string) => void; max: number;
  tier?: 'Expedited' | 'Standard' | 'Bulk'; onTier?: (v: 'Expedited' | 'Standard' | 'Bulk') => void; tiers?: Array<'Expedited' | 'Standard' | 'Bulk'>;
}) {
  return (
    <>
      <div className="field"><label htmlFor="b-restore-days">Restored files stay readable (days)</label>
        <input id="b-restore-days" className="input" type="number" min={1} max={max} placeholder={max === 10 ? '1' : '3'} value={days} onChange={(e) => onDays(e.target.value)} /></div>
      {tiers && onTier && (
        <div className="field"><label htmlFor="b-restore-tier">Default retrieval</label>
          <select id="b-restore-tier" className="select" value={tier} onChange={(e) => onTier(e.target.value as 'Expedited' | 'Standard' | 'Bulk')}>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
      )}
    </>
  );
}
